/**
 * ResetPassword — consumes ?token=... from the emailed reset link.
 * Validates client-side minimally; server is authoritative on token + policy.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Loader2, KeyRound, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('This reset link is missing a token.');
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!pw || pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords don\'t match.'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. Request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4">
      <div className="w-full max-w-md border border-[#2F8A92]/15 rounded-lg p-6 md:p-8 bg-[#0A0A0A]" data-testid="reset-password-page">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full border border-[#D4A84A]/30 flex items-center justify-center">
            <KeyRound size={22} className="text-[#D4A84A]" />
          </div>
        </div>
        <h1 className="font-historical text-[#D4A84A] text-2xl font-light tracking-tight text-center mb-1">
          Choose a new password
        </h1>
        <p className="font-hud text-[10px] tracking-widest text-[#2F8A92] uppercase text-center mb-6">
          ONE MINUTE — THEN YOU'RE BACK
        </p>

        {done ? (
          <div className="space-y-4 text-center" data-testid="reset-done">
            <CheckCircle2 size={40} className="text-[#2F8A92] mx-auto" />
            <h2 className="font-historical text-[#D4A84A] text-xl font-light">Password updated.</h2>
            <p className="font-mono text-[11px] text-white/80">Taking you to login…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
              <label className="font-hud text-[10px] tracking-widest uppercase text-white/80">NEW PASSWORD</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  autoFocus
                  required
                  data-testid="reset-password-input"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  className="hud-input w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#2F8A92]"
                  data-testid="reset-password-show"
                  aria-label="Show password"
                >
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-hud text-[10px] tracking-widest uppercase text-white/80">CONFIRM</label>
              <input
                type={show ? 'text' : 'password'}
                required
                data-testid="reset-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Same again"
                className="hud-input w-full"
              />
            </div>

            {error && (
              <p className="font-mono text-xs text-red-400 flex items-center gap-1" data-testid="reset-error">
                <AlertCircle size={11} /> {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              data-testid="reset-submit-btn"
              className="w-full font-hud text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] hover:bg-[#D4A84A]/85 py-3 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              UPDATE PASSWORD
            </button>

            <Link
              to="/"
              data-testid="reset-back-link"
              className="block text-center font-hud text-[10px] tracking-widest text-white/80 hover:text-[#2F8A92] uppercase"
            >
              BACK TO LOGIN
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
